/** Tempo + key of a song: detection (refined beat grid), snapping, tap tempo and the starting note. */
import { runAnalysis } from "@/lib/tempoKeyWorker";
import { playableUrl } from "@/lib/creatorApi";
import { absUrl } from "@/lib/recStudioApi";

export const NOTES = ["C", "Db", "D", "Eb", "E", "F", "F#", "G", "Ab", "A", "Bb", "B"];
const SR = 22050;
const MAX_SEC = 360;
const r3 = (v) => Math.round(v * 1000) / 1000;
export const clampBpm = (v) => Math.round(Math.max(40, Math.min(240, v)) * 100) / 100;
export const beatLen = (t) => 60 / t.bpm;
export const keyName = (t) => (t?.tonic == null ? null : `${NOTES[t.tonic]} ${t.mode}`);
const wrap = (v, p) => ((v % p) + p) % p;

/** Mono 22.05 kHz copy (the detectors need far less than full rate). */
async function mono(buffer) {
  const len = Math.max(1, Math.round(Math.min(buffer.duration, MAX_SEC) * SR));
  const off = new OfflineAudioContext(1, len, SR);
  const src = off.createBufferSource();
  src.buffer = buffer;
  src.connect(off.destination);
  src.start(0);
  return off.startRendering();
}

/** → { bpm, offset (first beat, s), tonic, mode, source: "auto", confidence } — the beat grid is offset + n·60/bpm. */
export async function analyzeTempoKey(buffer) {
  const m = await mono(buffer);
  return runAnalysis(m.getChannelData(0).slice(), SR);
}

/** Fetch + decode a song at the detector's rate (for pages that don't already hold the audio). */
export async function decodeSong(url) {
  const u = playableUrl(absUrl(url));
  const res = await fetch(u, u.startsWith(process.env.REACT_APP_BACKEND_URL) ? { credentials: "include" } : undefined);
  if (!res.ok) throw new Error(`Couldn't load the song (${res.status})`);
  return new OfflineAudioContext(1, 1, SR).decodeAudioData(await res.arrayBuffer());
}

/** First beat at or after `t` (song seconds). */
export const nextBeat = (tempo, t) => tempo.offset + Math.ceil((t - tempo.offset) / beatLen(tempo) - 1e-6) * beatLen(tempo);

/** Widen a selection out to whole beats (a little slack so a drag just past a beat doesn't add one). */
export function snapRange(tempo, { start, end }) {
  const p = beatLen(tempo);
  const a = Math.floor((start - tempo.offset) / p + 0.2);
  const b = Math.max(a + 1, Math.ceil((end - tempo.offset) / p - 0.2));
  return { start: r3(Math.max(0, tempo.offset + a * p)), end: r3(tempo.offset + b * p) };
}

/** Taps → { bpm } (straight-line fit of the last 8), plus the beat offset when the taps are on the song clock. */
export function fromTaps(taps, onSong) {
  if (taps.length < 4) return null;
  const last = taps.slice(-8);
  const mi = (last.length - 1) / 2;
  const mt = last.reduce((a, v) => a + v, 0) / last.length;
  const slope = last.reduce((a, t, i) => a + (i - mi) * (t - mt), 0) / last.reduce((a, _, i) => a + (i - mi) ** 2, 0);
  if (!(slope > 0)) return null;
  const bpm = clampBpm(60 / slope);
  if (!onSong) return { bpm };
  const p = 60 / bpm;
  let x = 0;
  let y = 0;
  last.forEach((t) => { const a = (2 * Math.PI * wrap(t, p)) / p; x += Math.cos(a); y += Math.sin(a); });
  return { bpm, offset: r3(wrap((Math.atan2(y, x) / (2 * Math.PI)) * p, p)) };
}

/** Same grid at a new tempo (keeps the first beat where it was). */
export const withBpm = (tempo, bpm) => ({ ...tempo, bpm: clampBpm(bpm), offset: r3(wrap(tempo.offset, 60 / clampBpm(bpm))), source: "manual" });

let noteCtx = null;
export const noteContext = () => { noteCtx = noteCtx || new (window.AudioContext || window.webkitAudioContext)(); return noteCtx; };

/** A soft keyboard-like tone on the key's home note (C4–B4) so you can find your pitch before you sing. */
export function playKeyNote(ctx, tonic) {
  if (ctx.state === "suspended") ctx.resume().catch(() => {});
  const f = 440 * 2 ** ((tonic - 9) / 12);
  const t = ctx.currentTime + 0.02;
  const g = ctx.createGain();
  g.gain.setValueAtTime(0, t);
  g.gain.linearRampToValueAtTime(0.32, t + 0.015);
  g.gain.exponentialRampToValueAtTime(0.0001, t + 1.8);
  g.connect(ctx.destination);
  [[f, "triangle", 1], [f * 2, "sine", 0.2]].forEach(([fr, type, lv]) => {
    const o = ctx.createOscillator();
    const og = ctx.createGain();
    o.type = type;
    o.frequency.value = fr;
    og.gain.value = lv;
    o.connect(og).connect(g);
    o.start(t);
    o.stop(t + 1.9);
  });
}
